import { useMutation, useQueryClient } from '@tanstack/react-query'
import { JsonRpcError } from '~/types'

/** Let one review check go for one account, or take that back.
 *
 *  The verdict is recomputed from the checks every time a row is triaged, so
 *  there is nothing to patch in the cache -- the forgiveness changes an input,
 *  not an output. Both verdict queries are invalidated instead and the row
 *  re-triages, which is also the only way to see the knock-on: forgiving the
 *  one finding that was holding a payout turns the row green, and a reviewer
 *  needs to see that happen rather than be told it.
 *
 *  **Moves no money.** This writes one row saying a human looked at a flag.
 *  Approving, declining and paying are untouched and still one at a time.
 */
const call = async (method: string, params: unknown) => {
    const resp = await fetch('/rpc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
    })
    const data = await resp.json()
    if (data.error) throw data
    // The service answers a refusal in the result rather than as a transport
    // error -- "say why" is not a protocol fault -- so it is raised here.
    if (data.result?.error) throw { error: { message: data.result.error } }
    return data.result
}

const useRetriage = () => {
    const queryClient = useQueryClient()
    return () => {
        queryClient.invalidateQueries(['withdraw_verdict'])
        queryClient.invalidateQueries(['withdraw_verdicts'])
    }
}

export const useForgiveFlag = () => {
    const retriage = useRetriage()
    return useMutation<any, JsonRpcError, any>({
        mutationFn: (params) => call('bank.withdraw_request.forgive_flag', params),
        onSuccess: retriage,
    })
}

export const useUnforgiveFlag = () => {
    const retriage = useRetriage()
    return useMutation<any, JsonRpcError, any>({
        mutationFn: (params) => call('bank.withdraw_request.unforgive_flag', params),
        onSuccess: retriage,
    })
}
